// src/pages/Workers/Absences/Absences.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Button
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchInput from "../../../components/SearchInput";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import { BASE_URL } from "../../../components/BaseUrl";
import CustomText, { textStyles } from "../../../components/CustomText";
import empty from "../../../assets/empty.png";
import { useTheme } from "../../../contexts/ThemeContext";
import EditAbsenceModal from "./EditAbsenceeModel";
import TruncatedCell from "../../../components/TruncatedCell.js";
import AbsenceFilterDrawer from "./AbsenceFilterDrawer.js";
import AddIcon from "@mui/icons-material/Add";
import AddAbsenceDrawer from "./AddAbsenceDrawer.js";
import ExportAbsencesPDF from "./AbsencePdf.js";
import ExportDropdown from "../../../components/ExportDropdown";
import { pdf } from "@react-pdf/renderer";
import ExportAbsencesExcel from "./AbsenceExcel.js";
import { useTranslation } from "react-i18next";
import { useCompanyProfile } from "../../../components/CompanyProfile";

const AbsenceTable = () => {
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { primaryColor, primaryTextColor } = useTheme();
const [openFilter, setOpenFilter] = useState(false);
const {t} =useTranslation()
const [filters, setFilters] = useState({
  absence_type: "",
  start_date: "",
  end_date: "",
});
  const isMobile = useMediaQuery("(max-width:768px)");
const [openEditModal, setOpenEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [absences, setAbsences] = useState([]);
const [openAddDrawer, setOpenAddDrawer] = useState(false);

  // Loaders
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const pageSize = 5;
  const [totalPages, setTotalPages] = useState(1);

  const firstLoadRef = useRef(true);
const handleDownloadExcel = async () => {
  const allData = await fetchAllAbsences();
  await ExportAbsencesExcel(allData);
};
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

const handleMenuClose = () => {
  setAnchorEl(null);
};

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(" ", " ")
      .replace(",", ",");
  };

  const prettySource = (str) => {
    if (!str) return "—";
    return str
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };


const fetchAbsences = useCallback(
  async ({ initial = false } = {}) => {
    console.log("🔍 FETCH CALLED — initial:", initial);
    console.log("🔍 Current filters at fetch time:", filters);
    console.log("🔍 Page:", page, "Page Size:", pageSize);

    try {
      if (initial) setInitialLoading(true);
      else setTableLoading(true);

      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(pageSize),
      });

      if (filters.absence_type) params.set("absence_type", filters.absence_type);
      if (filters.start_date) params.set("start_date", filters.start_date);
      if (filters.end_date) params.set("end_date", filters.end_date);

      console.log("🔍 Final URL:", `${BASE_URL}/absences/admin/absences?${params.toString()}`);

      const res = await fetch(`${BASE_URL}/absences/admin/absences?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      });

      const json = await res.json();
      console.log("🔍 API RESPONSE:", json);

      if (json.error) {
        setErrorMsg(json.message || "Failed to fetch absences.");
        setAbsences([]);
        return;
      }

      setAbsences(json.data?.absences || []);
      setTotalPages(json.data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setErrorMsg("Network error while fetching absences.");
    } finally {
      if (initial) setInitialLoading(false);
      else setTableLoading(false);
    }
  },
  [decryptedToken, page, pageSize, filters] // 🔥 added filters
);


useEffect(() => {
  console.log("🔄 useEffect TRIGGERED — page or filters changed");
  console.log("🔍 Filters inside useEffect:", filters);

  if (!decryptedToken) {
    setErrorMsg("Missing auth token.");
    setInitialLoading(false);
    return;
  }

  const isInitialLoad = firstLoadRef.current;
  fetchAbsences({ initial: isInitialLoad });

  if (isInitialLoad) firstLoadRef.current = false;
}, [decryptedToken, page, pageSize, filters, fetchAbsences]);

  const rows = Array.isArray(absences) ? absences : [];

  // FRONTEND SEARCH
  const filteredRows = rows.filter((row) => {
    const s = searchTerm.toLowerCase();

    const worker = row.worker?.name?.toLowerCase() || "";
    const dept = row.worker?.department?.toLowerCase() || "";
    const type = row.absence?.type?.name?.toLowerCase() || "";
    const source = row.absence?.source?.toLowerCase() || "";
    const start = formatDate(row.absence?.startDate).toLowerCase();
    const end = formatDate(row.absence?.endDate).toLowerCase();

    return (
      worker.includes(s) ||
      dept.includes(s) ||
      type.includes(s) ||
      source.includes(s) ||
      start.includes(s) ||
      end.includes(s)
    );
  });

  const isEmpty =
    !initialLoading &&
    !tableLoading &&
    !errorMsg &&
    filteredRows.length === 0;

    const fetchAllAbsences = async () => {
  try {
    const params = new URLSearchParams();

    if (filters.absence_type)
      params.set("absence_type", filters.absence_type);

    if (filters.start_date)
      params.set("start_date", filters.start_date);

    if (filters.end_date)
      params.set("end_date", filters.end_date);

    params.set("no_pagination", "true");

    const res = await fetch(
      `${BASE_URL}/absences/admin/absences?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      }
    );

    const json = await res.json();

    return json.data?.absences || [];
  } catch (e) {
    console.log("Error fetching ALL absences:", e);
    return [];
  }
};
const { logoBase64 } = useCompanyProfile(decryptedToken);

const handleDownloadPDF = async () => {
  const allData = await fetchAllAbsences();

  const blob = await pdf(
    <ExportAbsencesPDF data={allData} logo={logoBase64} />
  ).toBlob();

  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `Absence Report ${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`;
  a.click();

  URL.revokeObjectURL(url);
};


  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        py: 2,
        mx: 1,
        mt: 1,
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
        border: "2px solid #091E4224",
      }}
    >
      {/* Initial Loader */}
      {initialLoading ? (
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : errorMsg ? (
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", mt: 4 }}>
          <CustomText sx={{ color: "red" }}>{errorMsg}</CustomText>
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: isMobile ? "wrap" : "nowrap",
                            px: 2,
              pb: 2,
            }}
          >
                 <Box sx={{  display: "flex",alignItems:"center",gap:1,}}>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontWeight: 600,
                fontSize: "18px",
                color: "#003149",
              }}
            >
              {t("absence.heading")}
            </CustomText>

            {tableLoading && <CircularProgress size={20} />}
            </Box>

            <Box sx={{  display: "flex", justifyContent: "center" }}>
              <SearchInput
             placeholder={t("searchByName")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>

<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

  {/* ADD BUTTON */}
  <Button
    variant="contained"
    size="small"
    onClick={() => setOpenAddDrawer(true)}
    sx={{
      px: 2,
      backgroundColor: "#006EC2",
      height: 32,
      fontSize: 14,
      fontWeight: 400,
      fontFamily: "Poppins, sans-serif",
      borderRadius: "4px",
      textTransform: "none",
    }}
    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
  >
     {t("absence.add_button")}
  </Button>

 <ExportDropdown
    onDownloadPDF={handleDownloadPDF}
  onDownloadExcel={handleDownloadExcel}
  />

  {/* FILTER BUTTON (with clear icon if filters applied) */}
  {filters.absence_type || filters.start_date || filters.end_date ? (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: 1,
        bgcolor: primaryColor,
        cursor: "pointer",
      }}
      onClick={() => setOpenFilter(true)}
    >
      <FilterAltIcon sx={{ color: primaryTextColor, fontSize: 20 }} />

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          const cleared = {
            absence_type: "",
            start_date: "",
            end_date: "",
          };
          setFilters(cleared);
          fetchAbsences({ initial: false });
        }}
        sx={{
          position: "absolute",
          top: -6,
          right: -6,
          bgcolor: "white",
          width: 18,
          height: 18,
          border: "1px solid #C4C4C4",
          "&:hover": { bgcolor: "#f5f5f5" },
          zIndex: 10,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 14, color: "#333" }} />
      </IconButton>
    </Box>
  ) : (
    <IconButton
      onClick={() => setOpenFilter(true)}
      sx={{
        border: "1px solid #C4C4C4",
        borderRadius: 1,
        width: 34,
        height: 34,
      }}
    >
      <FilterAltIcon sx={{ fontSize: 20 }} />
    </IconButton>
  )}
</Box>

          </Box>



          {/* Empty State */}
          {isEmpty ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 5,
              }}
            >
              <img src={empty} alt="empty" style={{ width: 180 }} />
              <CustomText sx={{fontSize:"16px"}}>{t("absence.no_data")}</CustomText>
            </Box>
          ) : (
            <>
              {/* Table */}
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table>
                  <TableHead
                    sx={{
                      borderTop: "2px solid #091E4224",
                      borderBottom: "2px solid #091E4224",
                    }}
                  >
                    <TableRow>
                      <TableCell sx={headerCell}>{t("absence.id")}</TableCell>
                      <TableCell sx={headerCell}>{t("absence.worker")}</TableCell>
                      <TableCell sx={headerCell}>{t("absence.department")}</TableCell>
                      <TableCell sx={headerCell}>{t("absence.type")}</TableCell>
                    <TableCell sx={headerCell}>{t("absence.comments")}</TableCell>

                      <TableCell sx={headerCell}>{t("absence.start_date")}</TableCell>
                      <TableCell sx={headerCell}>{t("absence.end_date")}</TableCell>
                      <TableCell sx={headerCell}>{t("absence.source")}</TableCell>

                      {/* NEW ACTION COLUMN */}
                      <TableCell sx={headerCell} align="center">
                       {t("absence.actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={dataCell}>{row.id}</TableCell>
                        <TableCell sx={dataCell}>{row.worker?.name}</TableCell>
                        <TableCell sx={dataCell}>
                          {row.worker?.department}
                        </TableCell>
                        <TableCell sx={dataCell}>
                          {row.absence?.type?.name}
                        </TableCell>
                        <TableCell sx={dataCell}>
                          {/* {row.absence?.comment}
                           */}
                           <TruncatedCell text={row.absence?.comment} />
                        </TableCell>
                        <TableCell sx={dataCell}>
                          {formatDate(row.absence?.startDate)}
                        </TableCell>
                        <TableCell sx={dataCell}>
                          {formatDate(row.absence?.endDate)}
                        </TableCell>
                        <TableCell sx={dataCell}>
                          {prettySource(row.absence?.source)}
                        </TableCell>

                        {/* ACTION COLUMN */}
                        <TableCell sx={dataCell} align="center">
                          <IconButton onClick={(e) => handleMenuClick(e, row)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* MENU */}
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
<MenuItem
  onClick={() => {
    console.log("Opening modal for ID:", selectedRow?.id);
    setOpenEditModal(true);
    handleMenuClose();
  }}
  sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
>
  {t("absence.edit")}
</MenuItem>
                </Menu>
        
              </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(e, value) => setPage(value - 1)}
                  shape="rounded"
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                    },
                    "& .Mui-selected": {
                      backgroundColor: primaryColor,
                      color: primaryTextColor,
                    },
                  }}
                />
              </Box>

              {/* Pagination */}
      
            </>
          )}
          <EditAbsenceModal
  open={openEditModal}
  onClose={() => setOpenEditModal(false)}
  row={selectedRow}
  onUpdated={() => fetchAbsences()} // refresh table after update
/>


<AbsenceFilterDrawer
  open={openFilter}
  onClose={() => setOpenFilter(false)}
  onApply={(newFilters) => {
    console.log("Filter received from drawer:", newFilters);

    setFilters(newFilters);

    console.log("Filters saved in state:", newFilters);

    fetchAbsences({ initial: false });
  }}
/>
<AddAbsenceDrawer 
  open={openAddDrawer}
  onClose={() => setOpenAddDrawer(false)}
  onAdded={() => fetchAbsences({ initial: false })}
/>

        </>
      )}
    </Box>
    
  );
};

const headerCell = {
  color: "#44546F",
  fontSize: "15px",
  fontFamily: "Poppins",
  fontWeight: 600,
    whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const dataCell = {
  color: "#172B4D",
  fontSize: "13px",
  fontFamily: "Poppins",
  fontWeight: 400,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export default AbsenceTable;






// // src/pages/Workers/Absences/Absences.jsx
// import React, { useState, useEffect, useCallback, useRef } from "react";
// import {
//   Box,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Pagination,
//   CircularProgress,
//   useMediaQuery,
//   IconButton,
//   Menu,
//   MenuItem,
//   Button
// } from "@mui/material";
// import FilterAltIcon from "@mui/icons-material/FilterAlt";
// import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
// import MoreVertIcon from "@mui/icons-material/MoreVert";
// import SearchInput from "../../../components/SearchInput";
// import { useSelector } from "react-redux";
// import { decryptToken } from "../../../Utlis/Encryption";
// import { BASE_URL } from "../../../components/BaseUrl";
// import CustomText, { textStyles } from "../../../components/CustomText";
// import empty from "../../../assets/empty.png";
// import { useTheme } from "../../../contexts/ThemeContext";
// import EditAbsenceModal from "./EditAbsenceeModel";
// import TruncatedCell from "../../../components/TruncatedCell.js";
// import AbsenceFilterDrawer from "./AbsenceFilterDrawer.js";
// import AddIcon from "@mui/icons-material/Add";
// import AddAbsenceDrawer from "./AddAbsenceDrawer.js";
// import ExportAbsencesPDF from "./AbsencePdf.js";
// import ExportDropdown from "../../../components/ExportDropdown";
// import { pdf } from "@react-pdf/renderer";
// import ExportAbsencesExcel from "./AbsenceExcel.js";
// import { useTranslation } from "react-i18next";
// const AbsenceTable = () => {
//   const token = useSelector((state) => state.auth.token);
//   const decryptedToken = token ? decryptToken(token) : null;
//   const { primaryColor, primaryTextColor } = useTheme();
// const [openFilter, setOpenFilter] = useState(false);
// const {t} =useTranslation()
// const [filters, setFilters] = useState({
//   absence_type: "",
//   start_date: "",
//   end_date: "",
// });
//   const isMobile = useMediaQuery("(max-width:768px)");
// const [openEditModal, setOpenEditModal] = useState(false);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [absences, setAbsences] = useState([]);
// const [openAddDrawer, setOpenAddDrawer] = useState(false);

//   // Loaders
//   const [initialLoading, setInitialLoading] = useState(true);
//   const [tableLoading, setTableLoading] = useState(false);

//   const [errorMsg, setErrorMsg] = useState("");

//   // Pagination
//   const [page, setPage] = useState(0);
//   const pageSize = 5;
//   const [totalPages, setTotalPages] = useState(1);

//   const firstLoadRef = useRef(true);
// const handleDownloadExcel = async () => {
//   const allData = await fetchAllAbsences();
//   await ExportAbsencesExcel(allData);
// };
//   // Menu state
//   const [anchorEl, setAnchorEl] = useState(null);
//   const [selectedRow, setSelectedRow] = useState(null);

//   const handleMenuClick = (event, row) => {
//     setAnchorEl(event.currentTarget);
//     setSelectedRow(row);
//   };

// const handleMenuClose = () => {
//   setAnchorEl(null);
// };

//   const formatDate = (d) => {
//     if (!d) return "—";
//     const date = new Date(d);
//     return date
//       .toLocaleDateString("en-GB", {
//         day: "2-digit",
//         month: "short",
//         year: "numeric",
//       })
//       .replace(" ", " ")
//       .replace(",", ",");
//   };

//   const prettySource = (str) => {
//     if (!str) return "—";
//     return str
//       .split("_")
//       .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
//       .join(" ");
//   };


//   const fetchAbsences = useCallback(
//     async ({ initial = false } = {}) => {
//       try {
//         if (initial) setInitialLoading(true);
//         else setTableLoading(true);

//         setErrorMsg("");

    
//         const params = new URLSearchParams({
//   page: String(page + 1),
//   limit: String(pageSize),
// });

// if (filters.absence_type)
//   params.set("absence_type", filters.absence_type);

// if (filters.start_date)
//   params.set("start_date", filters.start_date);

// if (filters.end_date)
//   params.set("end_date", filters.end_date);

//         const res = await fetch(
//           `${BASE_URL}/absences/admin/absences?${params.toString()}`,
//           {
//             method: "GET",
//             headers: {
//               Authorization: `Bearer ${decryptedToken}`,
//               "Content-Type": "application/json",
//             },
//           }
//         );

//         const json = await res.json();

//         if (json.error) {
//           setErrorMsg(json.message || "Failed to fetch absences.");
//           setAbsences([]);
//           return;
//         }

//         setAbsences(json.data?.absences || []);
//         setTotalPages(json.data?.pagination?.totalPages || 1);
//       } catch (err) {
//         console.error("Fetch error:", err);
//         setErrorMsg("Network error while fetching absences.");
//       } finally {
//         if (initial) setInitialLoading(false);
//         else setTableLoading(false);
//       }
//     },
//     [decryptedToken, page, pageSize]
//   );

//   useEffect(() => {
//     if (!decryptedToken) {
//       setErrorMsg("Missing auth token.");
//       setInitialLoading(false);
//       return;
//     }

//     const isInitialLoad = firstLoadRef.current;
//     fetchAbsences({ initial: isInitialLoad });
//     if (isInitialLoad) firstLoadRef.current = false;
//   }, [decryptedToken, page, pageSize, fetchAbsences]);

//   const rows = Array.isArray(absences) ? absences : [];

//   // FRONTEND SEARCH
//   const filteredRows = rows.filter((row) => {
//     const s = searchTerm.toLowerCase();

//     const worker = row.worker?.name?.toLowerCase() || "";
//     const dept = row.worker?.department?.toLowerCase() || "";
//     const type = row.absence?.type?.name?.toLowerCase() || "";
//     const source = row.absence?.source?.toLowerCase() || "";
//     const start = formatDate(row.absence?.startDate).toLowerCase();
//     const end = formatDate(row.absence?.endDate).toLowerCase();

//     return (
//       worker.includes(s) ||
//       dept.includes(s) ||
//       type.includes(s) ||
//       source.includes(s) ||
//       start.includes(s) ||
//       end.includes(s)
//     );
//   });

//   const isEmpty =
//     !initialLoading &&
//     !tableLoading &&
//     !errorMsg &&
//     filteredRows.length === 0;

//     const fetchAllAbsences = async () => {
//   try {
//     const params = new URLSearchParams();

//     if (filters.absence_type)
//       params.set("absence_type", filters.absence_type);

//     if (filters.start_date)
//       params.set("start_date", filters.start_date);

//     if (filters.end_date)
//       params.set("end_date", filters.end_date);

//     params.set("no_pagination", "true");

//     const res = await fetch(
//       `${BASE_URL}/absences/admin/absences?${params.toString()}`,
//       {
//         method: "GET",
//         headers: {
//           Authorization: `Bearer ${decryptedToken}`,
//         },
//       }
//     );

//     const json = await res.json();

//     return json.data?.absences || [];
//   } catch (e) {
//     console.log("Error fetching ALL absences:", e);
//     return [];
//   }
// };

// const handleDownloadPDF = async () => {
//   const allData = await fetchAllAbsences();

//   const blob = await pdf(
//     <ExportAbsencesPDF data={allData} />
//   ).toBlob();

//   const url = URL.createObjectURL(blob);

//   const a = document.createElement("a");
//   a.href = url;
//   a.download = "absences-report.pdf";
//   a.click();

//   URL.revokeObjectURL(url);
// };

//   return (
//     <Box
//       sx={{
//         backgroundColor: "white",
//         borderRadius: 2,
//         py: 2,
//         mx: 1,
//         mt: 1,
//         minHeight: "400px",
//         display: "flex",
//         flexDirection: "column",
//         border: "2px solid #091E4224",
//       }}
//     >
//       {/* Initial Loader */}
//       {initialLoading ? (
//         <Box sx={{ flex: 1, display: "flex", justifyContent: "center", mt: 4 }}>
//           <CircularProgress />
//         </Box>
//       ) : errorMsg ? (
//         <Box sx={{ flex: 1, display: "flex", justifyContent: "center", mt: 4 }}>
//           <CustomText sx={{ color: "red" }}>{errorMsg}</CustomText>
//         </Box>
//       ) : (
//         <>
//           {/* Header */}
//           <Box
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               flexWrap: isMobile ? "wrap" : "nowrap",
//                             px: 2,
//               pb: 2,
//             }}
//           >
//                  <Box sx={{  display: "flex",alignItems:"center",gap:1,}}>
//             <CustomText
//               sx={{
//                 ...textStyles.h1,
//                 fontWeight: 600,
//                 fontSize: "18px",
//                 color: "#003149",
//               }}
//             >
//               {t("absence.heading")}
//             </CustomText>

//             {tableLoading && <CircularProgress size={20} />}
//             </Box>

//             <Box sx={{  display: "flex", justifyContent: "center" }}>
//               <SearchInput
//              placeholder={t("absence.search_placeholder")}
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//               />
//             </Box>

// <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

//   {/* ADD BUTTON */}
//   <Button
//     variant="contained"
//     size="small"
//     onClick={() => setOpenAddDrawer(true)}
//     sx={{
//       px: 2,
//       backgroundColor: "#006EC2",
//       height: 32,
//       fontSize: 14,
//       fontWeight: 400,
//       fontFamily: "Poppins, sans-serif",
//       borderRadius: "4px",
//       textTransform: "none",
//     }}
//     startIcon={<AddIcon sx={{ fontSize: 18 }} />}
//   >
//      {t("absence.add_button")}
//   </Button>

//  <ExportDropdown
//     onDownloadPDF={handleDownloadPDF}
//   onDownloadExcel={handleDownloadExcel}
//   />

//   {/* FILTER BUTTON (with clear icon if filters applied) */}
//   {filters.absence_type || filters.start_date || filters.end_date ? (
//     <Box
//       sx={{
//         position: "relative",
//         display: "inline-flex",
//         alignItems: "center",
//         justifyContent: "center",
//         width: 34,
//         height: 34,
//         borderRadius: 1,
//         bgcolor: primaryColor,
//         cursor: "pointer",
//       }}
//       onClick={() => setOpenFilter(true)}
//     >
//       <FilterAltIcon sx={{ color: primaryTextColor, fontSize: 20 }} />

//       <IconButton
//         size="small"
//         onClick={(e) => {
//           e.stopPropagation();
//           const cleared = {
//             absence_type: "",
//             start_date: "",
//             end_date: "",
//           };
//           setFilters(cleared);
//           fetchAbsences({ initial: false });
//         }}
//         sx={{
//           position: "absolute",
//           top: -6,
//           right: -6,
//           bgcolor: "white",
//           width: 18,
//           height: 18,
//           border: "1px solid #C4C4C4",
//           "&:hover": { bgcolor: "#f5f5f5" },
//           zIndex: 10,
//         }}
//       >
//         <CloseRoundedIcon sx={{ fontSize: 14, color: "#333" }} />
//       </IconButton>
//     </Box>
//   ) : (
//     <IconButton
//       onClick={() => setOpenFilter(true)}
//       sx={{
//         border: "1px solid #C4C4C4",
//         borderRadius: 1,
//         width: 34,
//         height: 34,
//       }}
//     >
//       <FilterAltIcon sx={{ fontSize: 20 }} />
//     </IconButton>
//   )}
// </Box>

//           </Box>



//           {/* Empty State */}
//           {isEmpty ? (
//             <Box
//               sx={{
//                 flex: 1,
//                 display: "flex",
//                 flexDirection: "column",
//                 alignItems: "center",
//                 py: 5,
//               }}
//             >
//               <img src={empty} alt="empty" style={{ width: 180 }} />
//               <CustomText>{t("absence.no_data")}</CustomText>
//             </Box>
//           ) : (
//             <>
//               {/* Table */}
//               <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
//                 <Table>
//                   <TableHead
//                     sx={{
//                       borderTop: "2px solid #091E4224",
//                       borderBottom: "2px solid #091E4224",
//                     }}
//                   >
//                     <TableRow>
//                       <TableCell sx={headerCell}>{t("absence.id")}</TableCell>
//                       <TableCell sx={headerCell}>{t("absence.worker")}</TableCell>
//                       <TableCell sx={headerCell}>{t("absence.department")}</TableCell>
//                       <TableCell sx={headerCell}>{t("absence.type")}</TableCell>
//                     <TableCell sx={headerCell}>{t("absence.comments")}</TableCell>

//                       <TableCell sx={headerCell}>{t("absence.start_date")}</TableCell>
//                       <TableCell sx={headerCell}>{t("absence.end_date")}</TableCell>
//                       <TableCell sx={headerCell}>{t("absence.source")}</TableCell>

//                       {/* NEW ACTION COLUMN */}
//                       <TableCell sx={headerCell} align="center">
//                        {t("absence.actions")}
//                       </TableCell>
//                     </TableRow>
//                   </TableHead>

//                   <TableBody>
//                     {filteredRows.map((row) => (
//                       <TableRow key={row.id}>
//                         <TableCell sx={dataCell}>{row.id}</TableCell>
//                         <TableCell sx={dataCell}>{row.worker?.name}</TableCell>
//                         <TableCell sx={dataCell}>
//                           {row.worker?.department}
//                         </TableCell>
//                         <TableCell sx={dataCell}>
//                           {row.absence?.type?.name}
//                         </TableCell>
//                         <TableCell sx={dataCell}>
//                           {/* {row.absence?.comment}
//                            */}
//                            <TruncatedCell text={row.absence?.comment} />
//                         </TableCell>
//                         <TableCell sx={dataCell}>
//                           {formatDate(row.absence?.startDate)}
//                         </TableCell>
//                         <TableCell sx={dataCell}>
//                           {formatDate(row.absence?.endDate)}
//                         </TableCell>
//                         <TableCell sx={dataCell}>
//                           {prettySource(row.absence?.source)}
//                         </TableCell>

//                         {/* ACTION COLUMN */}
//                         <TableCell sx={dataCell} align="center">
//                           <IconButton onClick={(e) => handleMenuClick(e, row)}>
//                             <MoreVertIcon />
//                           </IconButton>
//                         </TableCell>
//                       </TableRow>
//                     ))}
//                   </TableBody>
//                 </Table>

//                 {/* MENU */}
//                 <Menu
//                   anchorEl={anchorEl}
//                   open={Boolean(anchorEl)}
//                   onClose={handleMenuClose}
//                 >
// <MenuItem
//   onClick={() => {
//     console.log("Opening modal for ID:", selectedRow?.id);
//     setOpenEditModal(true);
//     handleMenuClose();
//   }}
//   sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}
// >
//   {t("absence.edit")}
// </MenuItem>
//                 </Menu>
        
//               </TableContainer>

//                 <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
//                 <Pagination
//                   count={totalPages}
//                   page={page + 1}
//                   onChange={(e, value) => setPage(value - 1)}
//                   shape="rounded"
//                   size="small"
//                   sx={{
//                     "& .MuiPaginationItem-root": {
//                       fontFamily: "Poppins, sans-serif",
//                       fontSize: 13,
//                       fontWeight: 500,
//                     },
//                     "& .Mui-selected": {
//                       backgroundColor: primaryColor,
//                       color: primaryTextColor,
//                     },
//                   }}
//                 />
//               </Box>

//               {/* Pagination */}
      
//             </>
//           )}
//           <EditAbsenceModal
//   open={openEditModal}
//   onClose={() => setOpenEditModal(false)}
//   row={selectedRow}
//   onUpdated={() => fetchAbsences()} // refresh table after update
// />


// <AbsenceFilterDrawer
//   open={openFilter}
//   onClose={() => setOpenFilter(false)}
//   onApply={(newFilters) => {
//     console.log("Filter received from drawer:", newFilters);

//     setFilters(newFilters);

//     console.log("Filters saved in state:", newFilters);

//     fetchAbsences({ initial: false });
//   }}
// />
// <AddAbsenceDrawer 
//   open={openAddDrawer}
//   onClose={() => setOpenAddDrawer(false)}
//   onAdded={() => fetchAbsences({ initial: false })}
// />

//         </>
//       )}
//     </Box>
    
//   );
// };

// const headerCell = {
//   color: "#44546F",
//   fontSize: "15px",
//   fontFamily: "Poppins",
//   fontWeight: 600,
//     whiteSpace: "nowrap",
//   overflow: "hidden",
//   textOverflow: "ellipsis",
// };

// const dataCell = {
//   color: "#172B4D",
//   fontSize: "13px",
//   fontFamily: "Poppins",
//   fontWeight: 400,
//   whiteSpace: "nowrap",
//   overflow: "hidden",
//   textOverflow: "ellipsis",
// };

// export default AbsenceTable;
